import React from 'react';
import { TextField, Typography, Box, Grid, Button, Paper, Stack, Chip } from '@mui/material';

export const VIEWER_COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  surfaceBorder: '#dbe7f3',
  surfaceSoft: '#e2e8f0',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
};

export const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 4,
  border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
  background: '#ffffff',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#d0d7de' },
    '&:hover fieldset': { borderColor: VIEWER_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: VIEWER_COLORS.primary, borderWidth: 2 },
  },
};

const ReadOnlyField = ({ label, value }) => (
  <TextField label={label} fullWidth value={value} InputProps={{ readOnly: true }} size="small" sx={fieldSx} />
);

export const CuestionarioVentHero = ({ createdAt }) => (
  <Paper
    className="entrevista-viewer__hero"
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      mb: 1,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1}
    >
      <Box>
        <Typography variant="overline" sx={{ color: VIEWER_COLORS.primaryDark, letterSpacing: 1.4 }}>
          Sistema de seguimiento clínico
        </Typography>
        <Typography variant="h4" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
          Cuestionario VENT
        </Typography>
        <Typography sx={{ color: VIEWER_COLORS.textMuted, maxWidth: 760 }}>
          Valoración de hábitos tabáquicos, actividad física y función pulmonar.
        </Typography>
      </Box>
      <Chip
        label={`Registro ${createdAt || '--/--/----'}`}
        sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }}
        variant="outlined"
      />
    </Stack>
  </Paper>
);

export const PatientProfileSection = ({ patientInfo }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Perfil del paciente
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Nombre" value={patientInfo.nombre} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Apellidos" value={patientInfo.apellidos} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ReadOnlyField label="Fecha" value={patientInfo.createdAt} />
      </Grid>
    </Grid>
  </Paper>
);

export const TabaquismoSection = ({ fields }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Tabaquismo
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ReadOnlyField label="¿Es fumador?" value={fields.fumador} />
      </Grid>
      {fields.fumador === 'si' && (
        <Grid item xs={12} md={6}>
          <ReadOnlyField label="Cigarros por día" value={fields.cigarrosDia} />
        </Grid>
      )}
      {fields.fumador === 'lo-fue' && (
        <Grid item xs={12} md={6}>
          <ReadOnlyField label="Años desde la deshabituación" value={fields.aniosDeshabituacion} />
        </Grid>
      )}
    </Grid>
  </Paper>
);

export const ActividadFisicaSection = ({ fields }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Actividad física
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ReadOnlyField label="¿Realiza ejercicio físico?" value={fields.ejercicio} />
      </Grid>
      {fields.ejercicio === 'si' && (
        <Grid item xs={12} md={6}>
          <ReadOnlyField label="Tipo de actividad física" value={fields.actividadFisica} />
        </Grid>
      )}
    </Grid>
  </Paper>
);

export const FuncionPulmonarSection = ({ fields }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Función pulmonar
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ReadOnlyField label="PEF (l/min)" value={fields.pef} />
      </Grid>
    </Grid>
  </Paper>
);

export const CuestionarioViewerActions = ({ onExportPDF, onEdit }) => (
  <Paper
    className="entrevista-viewer__actions"
    elevation={0}
    sx={{
      mt: 2,
      p: 2,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(10px)',
    }}
  >
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
      <Button variant="outlined" color="primary" onClick={onEdit} sx={{ borderRadius: 2.5 }}>
        Editar
      </Button>
      <Button variant="contained" color="secondary" onClick={onExportPDF} sx={{ borderRadius: 2.5 }}>
        Exportar PDF
      </Button>
    </Stack>
  </Paper>
);
